// Universal tax calculation controller — works with any list of taxes.
//
// Schema (company.tax_config):
//   taxes: [{ key, label, rate %, base: "gross"|"after_deductions", apply_to: "employee"|"employer",
//             enabled, min_base_mzp, max_base_mzp, deduction_keys }]
//   deductions: [{ key, label, type: "absolute"|"percent_gross", value, unit: "tenge"|"mzp"|"mrp", enabled }]
//   ref_values: [{ year, mzp, mrp }]  — yearly МЗП/МРП reference table
//
// Logic:
//   - For each enabled tax, compute base:
//       deduction_keys (array)     → base -= only the listed deductions
//       else base "after_deductions" → base -= all enabled deductions (legacy)
//       else                          → base = taxable gross
//   - Base is clamped to [min_base_mzp × МЗП, max_base_mzp × МЗП] (0 = no limit).
//     Min clamp applies only when income > 0 (no income — no tax).
//   - Absolute deductions support units: tenge (₸), mzp (× МЗП), mrp (× МРП).
//   - МЗП/МРП resolved per-year from ref_values (greatest year <= requested).
//   - net = totalGross - sum of employee-side taxes.
//   - employer-side taxes shown but not subtracted from net.
//
// Backward-compat: if `taxes` is missing, synthesize from legacy ipn/opv/vosms/so fields.

const FALLBACK_MZP = 85000;
const FALLBACK_MRP = 4325;

/** Resolve { mzp, mrp } for a given year from cfg.ref_values. */
export function resolveRefs(cfg, year) {
  const y = Number(year) || new Date().getFullYear();
  const rows = (Array.isArray(cfg?.ref_values) ? cfg.ref_values : [])
    .filter(r => r && Number(r.year) > 0);
  let best = null;
  for (const r of rows) {
    const ry = Number(r.year);
    if (ry <= y && (!best || ry > Number(best.year))) best = r;
  }
  if (!best && rows.length) {
    best = rows.reduce((a, b) => (Number(a.year) <= Number(b.year) ? a : b));
  }
  return {
    mzp: Number(best?.mzp) || Number(cfg?.mzpAmount) || FALLBACK_MZP,
    mrp: Number(best?.mrp) || FALLBACK_MRP,
  };
}

function getTaxes(cfg) {
  if (Array.isArray(cfg.taxes)) {
    return cfg.taxes.map(t => ({
      key: t.key,
      label: t.label || t.key,
      rate: Number(t.rate) || 0,
      base: t.base || "gross",
      apply_to: t.apply_to || "employee",
      enabled: t.enabled !== false,
      min_base_mzp: Number(t.min_base_mzp) || 0,
      max_base_mzp: Number(t.max_base_mzp) || 0,
      deduction_keys: Array.isArray(t.deduction_keys) ? t.deduction_keys : null,
    }));
  }
  // Legacy fallback
  const out = [];
  if (cfg.opv_enabled   !== false) out.push({ key: "opv",   label: cfg.opv_label   || "ОПВ",   rate: Number(cfg.opv ?? 10),   base: "gross",            apply_to: "employee", enabled: true, min_base_mzp: 0, max_base_mzp: 0, deduction_keys: null });
  if (cfg.vosms_enabled !== false) out.push({ key: "vosms", label: cfg.vosms_label || "ВОСМС", rate: Number(cfg.vosms ?? 2),  base: "gross",            apply_to: "employee", enabled: true, min_base_mzp: 0, max_base_mzp: 0, deduction_keys: null });
  if (cfg.ipn_enabled   !== false) out.push({ key: "ipn",   label: cfg.ipn_label   || "ИПН",   rate: Number(cfg.ipn ?? 10),   base: "after_deductions", apply_to: "employee", enabled: true, min_base_mzp: 0, max_base_mzp: 0, deduction_keys: null });
  if (cfg.so_enabled    !== false) out.push({ key: "so",    label: cfg.so_label    || "СО",    rate: Number(cfg.so ?? 3.5),   base: "gross",            apply_to: "employer", enabled: true, min_base_mzp: 0, max_base_mzp: 0, deduction_keys: null });
  return out;
}

function getDeductions(cfg) {
  // Explicit empty array means "no deductions" — respect it (used by bonusGrossFromNet
  // for marginal bonus solving). Only fall back to legacy synthesis when the key is missing.
  if (Array.isArray(cfg.deductions)) {
    return cfg.deductions;
  }
  const out = [];
  if (cfg.useMzpDeduction !== false) {
    out.push({ key: "mzp", label: "Вычет 1 МЗП", type: "absolute", value: cfg.mzpAmount ?? 85000, unit: "tenge", enabled: true });
  }
  if (cfg.useOpvDeduction !== false) {
    out.push({ key: "opv_pct", label: "Вычет ОПВ", type: "percent_gross", value: cfg.opv ?? 10, enabled: true });
  }
  if (cfg.useVosmsDeduction !== false) {
    out.push({ key: "vosms_pct", label: "Вычет ВОСМС", type: "percent_gross", value: cfg.vosms ?? 2, enabled: true });
  }
  return out;
}

// `extras` = { total: Σ, taxableByKey: { taxKey: Σ_taxable_for_this_tax } }
// `opts`   = { year } — period year for МЗП/МРП resolution (default: current year)
export function computeTaxes(gross, taxConfig, extras = null, opts = null) {
  const cfg = taxConfig || {};
  const taxes = getTaxes(cfg).filter(t => t.enabled);
  const deductions = getDeductions(cfg);
  const { mzp, mrp } = resolveRefs(cfg, opts?.year);

  const ex = extras || { total: 0, taxableByKey: {} };
  const exTotal = Number(ex.total) || 0;
  const exTaxable = (key) => Number((ex.taxableByKey || {})[key]) || 0;

  const dedAmount = (d, taxableGross) => {
    if (d.type === "percent_gross") return taxableGross * ((Number(d.value) || 0) / 100);
    const unit = d.unit || "tenge";
    const mul = unit === "mzp" ? mzp : unit === "mrp" ? mrp : 1;
    return (Number(d.value) || 0) * mul;
  };

  const dedsFor = (t) => {
    if (Array.isArray(t.deduction_keys)) {
      const ks = new Set(t.deduction_keys);
      return deductions.filter(d => d.enabled !== false && ks.has(d.key));
    }
    if (t.base === "after_deductions") return deductions.filter(d => d.enabled !== false);
    return [];
  };

  // byTax: { key: { label, rate, base, apply_to, amount } }
  const byTax = {};
  let employeeTotal = 0;

  for (const t of taxes) {
    const taxableGross = gross + exTaxable(t.key);
    let base = 0;
    if (taxableGross > 0) {
      base = taxableGross;
      for (const d of dedsFor(t)) base -= dedAmount(d, taxableGross);
      base = Math.max(0, base);
      if (t.min_base_mzp > 0) base = Math.max(base, t.min_base_mzp * mzp);
      if (t.max_base_mzp > 0) base = Math.min(base, t.max_base_mzp * mzp);
    }
    const amount = base * (t.rate / 100);
    byTax[t.key] = {
      label: t.label, rate: t.rate, base: t.base, apply_to: t.apply_to,
      amount: Math.round(amount * 100) / 100,
      taxableBase: Math.round(base * 100) / 100,
    };
    if (t.apply_to === "employee") employeeTotal += amount;
  }

  const totalGross = gross + exTotal;
  const net = totalGross - employeeTotal;
  const r = (n) => Math.round(n * 100) / 100;

  // Legacy aliases for code that still reads tax.ipn / tax.opv / tax.vosms / tax.so directly.
  const legacy = {};
  for (const k of ["ipn", "opv", "vosms", "so"]) {
    legacy[k] = byTax[k]?.amount ?? 0;
  }

  return {
    gross: r(gross),
    extrasTotal: r(exTotal),
    totalGross: r(totalGross),
    employeeTotal: r(employeeTotal),
    byTax,
    net: r(net),
    ...legacy,
    // taxableBase for "income tax" — first after-deductions tax (for legacy widgets)
    taxableBase: (() => {
      const inc = Object.values(byTax).find(b => b.base === "after_deductions");
      return inc ? inc.taxableBase : 0;
    })(),
  };
}

// Convenience: list enabled taxes split into employee-paid and employer-paid.
export function listTaxes(taxConfig) {
  return getTaxes(taxConfig || {}).filter(t => t.enabled);
}

/**
 * Solve the inverse problem: given desired NET payout and tax config,
 * find the gross such that computeTaxes(gross).net === target.
 *
 * Universal — works with any tax list (base=gross|after_deductions, any deductions,
 * any apply_to=employee). Employer-side taxes are excluded from the equation
 * since they don't affect net.
 *
 * Implementation: iterative refinement. Income taxes can be piecewise linear
 * (max(0, base) clamp + base min/max caps), so a small Newton-style loop
 * converges in a few steps. Returns gross rounded to 2 decimals.
 */
export function solveGrossFromNet(targetNet, taxConfig, opts = null) {
  const target = Number(targetNet) || 0;
  if (target <= 0) return 0;
  let gross = target;        // first guess: gross == net (under-estimates)
  for (let i = 0; i < 40; i++) {
    const r = computeTaxes(gross, taxConfig, null, opts);
    const delta = target - r.net;
    if (Math.abs(delta) < 0.005) break;
    // r.net is monotonically increasing in gross, so adding delta is a safe step.
    gross += delta;
    if (gross < 0) gross = 0;
  }
  return Math.round(gross * 100) / 100;
}

/**
 * Lookup tax-exempt keys for this user based on their social_status_id.
 * Returns [] if no status, status disabled, or status not found in company.
 */
export function userExemptTaxKeys(user, company) {
  const sid = user?.social_status_id;
  if (!sid) return [];
  const list = company?.social_statuses || [];
  const s = list.find(x => x?.id === sid);
  if (!s || s.enabled === false) return [];
  return Array.isArray(s.non_taxable_keys) ? s.non_taxable_keys.slice() : [];
}

/**
 * Build a per-user effective tax_config by removing taxes exempt for this user.
 * If the user has no exemptions, returns the original cfg unchanged.
 */
export function userEffectiveTaxConfig(user, company, taxConfig) {
  const exempt = userExemptTaxKeys(user, company);
  if (exempt.length === 0) return taxConfig;
  const ex = new Set(exempt);
  const allTaxes = getTaxes(taxConfig || {});
  return {
    ...(taxConfig || {}),
    taxes: allTaxes.filter(t => !ex.has(t.key)).map(t => ({ ...t })),
  };
}

/**
 * Compute the gross salary for a user, taking salary_mode and social_status into account.
 * Returns the "monthly gross" — the salary that should drive timesheet
 * proportional calculations.
 */
export function userMonthlyGross(user, taxConfig, company = null, opts = null) {
  const salary = Number(user?.salary) || 0;
  if (user?.salary_mode === "net") {
    const ucfg = company ? userEffectiveTaxConfig(user, company, taxConfig) : taxConfig;
    return solveGrossFromNet(salary, ucfg, opts);
  }
  return salary;
}

/**
 * Inverse problem for a bonus / allowance line: given desired NET payout
 * for THIS bonus (in addition to the base salary), find the gross amount
 * that yields that net after taxes.
 *
 * Important: bonuses are treated as MARGINAL income — the per-employee
 * MZP/personal deductions are assumed already exhausted by the base salary,
 * so we strip absolute deductions from the config. Taxes excluded via
 * `nonTaxableKeys` are skipped entirely.
 *
 * Returns gross rounded to 2 decimals.
 */
export function bonusGrossFromNet(netAmount, taxConfig, nonTaxableKeys = [], opts = null) {
  const net = Number(netAmount) || 0;
  if (net <= 0) return 0;
  const nt = new Set(nonTaxableKeys || []);
  const allTaxes = getTaxes(taxConfig || {});
  // Keep percent_gross deductions (they apply marginally to the bonus too)
  // Drop absolute deductions (MZP/МРП) — already absorbed by base salary.
  const allDeds = (taxConfig?.deductions);
  const margDeds = Array.isArray(allDeds)
    ? allDeds.filter(d => d?.type !== "absolute")
    : [
        { key: "opv_pct",   label: "Вычет ОПВ",   type: "percent_gross", value: Number(taxConfig?.opv ?? 10),  enabled: taxConfig?.useOpvDeduction !== false },
        { key: "vosms_pct", label: "Вычет ВОСМС", type: "percent_gross", value: Number(taxConfig?.vosms ?? 2), enabled: taxConfig?.useVosmsDeduction !== false },
      ];
  const cfg = {
    ...(taxConfig || {}),
    deductions: margDeds,
    taxes: allTaxes
      .filter(t => !nt.has(t.key))
      .map(t => ({ ...t })),
  };
  return solveGrossFromNet(net, cfg, opts);
}
